import { useState } from 'react';
import { useSendTransaction } from 'wagmi';

import { Potion, PotionTypes, Warrior } from '~/../api/common.types';
import VerifyWarriorsWallet from '~/components/auth/VerifyWallet';
// import { Potion } from '~/../api/common.types';
import Button from '~/components/common/Button';
import Logo from '~/components/common/Logo';
import Section from '~/components/hero/LandingPage/Section';
import MainNav from '~/components/navbar/MainNav';
import DeadWarriors from '~/components/revival/DeadWarriors';
import Potions from '~/components/revival/Potions';
import ReviveWarriors from '~/components/revival/ReviveWarriors';
import SuccessWarrior from '~/components/revival/SuccessWarrior';
import { OPENSEA_POTION_COLLECTION_URL } from '~/constants';
import { ConnectedProviders } from '~/contextProviders/connectedProviders';
import { useGameAuthentication } from '~/contextProviders/GameAuthenticationProvider';
import { useWarriorAuthentication } from '~/contextProviders/WarriorAuthenticationProvider';
import { useGetIsApproved } from '~/hooks/revival/getApproval';
import { useGetPotionNFTs } from '~/hooks/revival/getPotionQuery';
import { useSetApproval } from '~/hooks/revival/setIsApproval';
import { useConfirmTxn } from '~/hooks/revival/useConfirmRevival';
import { useSetRevival } from '~/hooks/revival/useRevival';
import { useSetTransaction } from '~/hooks/revival/useSetTransaction';
import { useGetAccount } from '~/hooks/web3/useGetAccount';
import { getCurrentWarrior, potionType } from '~/utils/utils';

interface RequirementsResponse {
  isValid: boolean;
  levelsRemaining: number;
  deadWarriorsNeeded: boolean;
}

function calculateRequirements({
  reviveId,
  potionId,
  deadWarriorIds,
  potions,
  deadWarriors,
}: {
  reviveId: string | null;
  potionId: string | null;
  deadWarriorIds: Array<string> | null;
  potions: Array<Potion> | undefined;
  deadWarriors: Array<Warrior> | undefined;
}): RequirementsResponse {
  let isValid = false;
  let levelsRemaining = 0;
  let deadWarriorsNeeded = true;
  const potion = potions?.find((pot) => pot.id.toString() == potionId);
  const reviveWarrior = deadWarriors?.find(
    (warrior) => warrior.warrior_id.toString() === reviveId
  );
  const deadWarriorsSelected = deadWarriors?.filter((warrior) =>
    deadWarriorIds?.includes(warrior.warrior_id.toString())
  );
  if (!potion || !reviveWarrior) {
    return {
      isValid,
      levelsRemaining,
      deadWarriorsNeeded,
    };
  }

  const potionColor = potionType(potion);
  if (potionColor === PotionTypes.blue) {
    isValid = true;
    deadWarriorsNeeded = false;
    return {
      isValid,
      levelsRemaining,
      deadWarriorsNeeded,
    };
  }

  const reviveWarriorData = getCurrentWarrior(reviveWarrior);
  if (potionColor === PotionTypes.green && reviveWarriorData.level <= 2) {
    isValid = true;
    deadWarriorsNeeded = false;
    return {
      isValid,
      levelsRemaining,
      deadWarriorsNeeded,
    };
  } else if (
    potionColor === PotionTypes.purple &&
    reviveWarriorData.level <= 1
  ) {
    isValid = true;
    deadWarriorsNeeded = false;
    return {
      isValid,
      levelsRemaining,
      deadWarriorsNeeded,
    };
  }

  deadWarriorsNeeded = true;
  const deadLevelsTotal = deadWarriorsSelected?.length ?
    deadWarriorsSelected
      .map((warrior) => getCurrentWarrior(warrior).level)
      .reduce((prev, curr) => prev + curr, 0) :
    0;
  levelsRemaining = reviveWarriorData.level - deadLevelsTotal;
  if (levelsRemaining > 0) {
    isValid = false;
  } else {
    isValid = true;
  }

  return {
    isValid,
    levelsRemaining,
    deadWarriorsNeeded,
  };
}

// TODO: Break this into sub components
const RevivalPage = () => {
  const {
    warriors: deadWarriors,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useWarriorAuthentication();
  const { accessToken } = useGameAuthentication();

  const { data: connectedAccount } = useGetAccount();
  const { data: potions, isLoading: potionIsLoading } = useGetPotionNFTs({
    walletIsConnected: !!connectedAccount?.connected,
  });

  const [reviveId, setReviveId] = useState<string | null>(null);
  const [potionId, setPotionId] = useState<string | null>(null);

  const [reviveMessage, setReviveMessage] = useState<string>('');
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [deadWarriorIds, setDeadWarriorIds] = useState<string[]>([]);
  const { mutate: setRevivalMutation } = useSetRevival();
  const { mutate: setTransactionMutation } = useSetTransaction();
  const [txnHash, setTxnHash] = useState<string | null>(null);
  // const { mutate: setApprovalMutation } = useSetApproval();
  const { isSuccess: isSetApprovalSuccess, write: setApprovalMutation } = useSetApproval();

  const { data: confirmedWarrior } = useConfirmTxn({
    reviveId: reviveId,
    potionId: potionId,
    deadWarriorIds: deadWarriorIds,
    token: accessToken || '',
    txnHash: txnHash,
  });

  const requirements = calculateRequirements({
    reviveId: reviveId,
    potionId: potionId,
    deadWarriorIds: deadWarriorIds,
    potions: potions,
    deadWarriors: deadWarriors,
  });

  const { data: isApproved, refetch: refetchApproval } = useGetIsApproved({
    enabled: Boolean(connectedAccount?.address) && Boolean(requirements.deadWarriorsNeeded),
  });

  if (isSetApprovalSuccess) {
    refetchApproval({ cancelRefetch: isApproved as boolean });
  }

  const { sendTransactionAsync } = useSendTransaction({
    mode: 'recklesslyUnprepared',
  });

  const handleSetRevival = () => {
    // todo: clean up nested onSuccess hooks
    if (!potionId || !reviveId) return;

    setReviveMessage('Reviving...');
    setRevivalMutation(
      {
        reviveId: reviveId,
        potionId: potionId,
        deadWarriorIds: deadWarriorIds,
        token: accessToken || '',
      },
      {
        onSuccess: (data) => {
          setReviveMessage('Waiting for confirmation...');
          setTransactionMutation(
            {
              data: data,
              sendTransactionAsync: sendTransactionAsync,
            },
            {
              onSuccess: (data: string) => {
                setReviveMessage('Confirming...');
                setTxnHash(data);
              },
              onError: (err) => {
                setReviveMessage('');
                console.log('err setTransactionMutation', err);
              },
            }
          );
        },
        onError: (err) => {
          console.log('err', err);
        },
      }
    );
  };

  const handleSetApproval = async () => {
    setIsApproving(true);
    setApprovalMutation?.();
  };

  return (
    <>
      <div className="container container--landing-page container--revival">
        <header className="landing-page__header">
          <MainNav showConnectWallet={true} />
        </header>
        <div className="content-wrapper">
          <Section className="landing-page__section--hero">
            <div className="main-logo">
              <Logo filename="revival-logo.svg" alt="Revival" />
            </div>
          </Section>
          {confirmedWarrior && <SuccessWarrior warrior={confirmedWarrior} />}

          {!confirmedWarrior && (
            <>
              <h2 className="hd-keyline">1</h2>
              <h3 className="revival-headline">Choose a soul to revive</h3>
              {accessToken ? (
                <>
                  {(isLoading || isLoadingMore) && (
                    <div>Loading dead Warriors...</div>
                  )}
                  {!(isLoading || isLoadingMore) && !deadWarriors?.length && (
                    <div>You don&apos;t own any dead Warriors</div>
                  )}
                </>
              ) : (
                <div className="name-wallet-connect">
                  <VerifyWarriorsWallet />
                </div>
              )}
              <ReviveWarriors
                deadWarriors={deadWarriors}
                reviveId={reviveId}
                setReviveId={setReviveId}
                setDeadWarriorIds={setDeadWarriorIds}
              />
              {hasMore && (
                <div className="revival__load-more">
                  <Button
                    label={isLoadingMore ? 'Loading...' : 'Load More'}
                    onClick={loadMore}
                    disabled={isLoadingMore}
                    className="warrior__attack__confirm__button warrior__attack__confirm__button--more primary"
                  />
                </div>
              )}

              <h2 className="hd-keyline">2</h2>
              <h3 className="revival-headline">Choose a potion</h3>
              {potionIsLoading && <div>Loading Potions...</div>}
              {!potionIsLoading && !potions?.length && (
                <div>
                  You don&apos;t own any potions. Potions can be purchased on{' '}
                  <a
                    className="underline"
                    href={OPENSEA_POTION_COLLECTION_URL}
                  >
                    opensea
                  </a>
                  .
                </div>
              )}
              <Potions
                potions={potions}
                setPotionId={setPotionId}
                potionId={potionId}
                setDeadWarriorIds={setDeadWarriorIds}
              />
              <p className="grid-potions__text grid-potions__explainer">
                {' '}
                <img
                  src="/img/potion-blue200.png"
                  alt="Potion"
                  className="potion-img__explainer"
                />
                Blue: Level 1 - 5: Free
              </p>
              <p className="grid-potions__text grid-potions__explainer">
                {' '}
                <img
                  src="/img/potion-green200.png"
                  alt="Potion"
                  className="potion-img__explainer"
                />
                Green: Level 1 - 2: Free, L3 - 5: Dead Warriors whose levels add
                up to the level of the Warrior being revived
              </p>
              <p className="grid-potions__text grid-potions__explainer">
                {' '}
                <img
                  src="/img/potion-purple200.png"
                  alt="Potion"
                  className="potion-img__explainer"
                />
                Purple: Level 1: Free, L2 - 5: Dead Warriors whose levels add up
                to the level of the Warrior being revived
              </p>

              <h2 className="hd-keyline">3</h2>
              <h3 className="revival-headline">Choose dead souls</h3>

              {requirements.levelsRemaining > 0 && (
                <h3 className="revival-headline">
                  Select Dead Souls that add up to{' '}
                  {requirements.levelsRemaining} levels
                </h3>
              )}

              {isApproved ? (
                <>
                  <DeadWarriors
                    reviveId={reviveId}
                    deadWarriors={deadWarriors}
                    deadWarriorIds={deadWarriorIds}
                    deadWarriorsNeeded={requirements.deadWarriorsNeeded}
                    setDeadWarriorIds={setDeadWarriorIds}
                  />
                  {hasMore && (
                    <div className="revival__load-more">
                      <Button
                        label={isLoadingMore ? 'Loading...' : 'Load More'}
                        onClick={loadMore}
                        disabled={isLoadingMore}
                        className="warrior__attack__confirm__button warrior__attack__confirm__button--more primary"
                      />
                    </div>
                  )}
                </>
              ) : (
                <Button
                  label={
                    isApproving ? 'Approving...' : 'Approve Warriors Access'
                  }
                  onClick={handleSetApproval}
                  disabled={
                    !connectedAccount?.connected || isApproving ? true : false
                  }
                  className="warrior__attack__confirm__button warrior__attack__confirm__button--more primary"
                />
              )}
              {requirements.isValid ? (
                reviveMessage ? (
                  <>
                    <div className="revival__load-more">
                      <button
                        className="revival-button button"
                        type="button"
                        disabled={true}
                      >
                        {reviveMessage}
                      </button>
                    </div>
                    <small className="revival-warning">
                      *Do not hit the back button or refresh while the
                      transaction is pending.
                    </small>
                  </>
                ) : (
                  <>
                    <div className="revival__load-more">
                      <button
                        className="revival-button button"
                        type="button"
                        onClick={handleSetRevival}
                      >
                        Revive Peacefall #{reviveId}
                      </button>
                    </div>
                    <small className="revival-warning">
                      *Do not hit the back button or refresh while the
                      transaction is pending.
                    </small>
                  </>
                )
              ) : (
                <>
                  <div className="revival__load-more">
                    <button
                      className="revival-button button"
                      type="button"
                      disabled
                    >
                      Revive Warrior
                    </button>
                  </div>
                </>
              )}
              <small className="revival-note">
                * Will require MetaMask signing to burn your warriors. This is
                final.
              </small>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const Revival = () => {
  return (
    <ConnectedProviders accessTokenRequired={true}>
      <RevivalPage />
    </ConnectedProviders>
  );
};

export default Revival;
